import React, { useCallback, useMemo, useState } from 'react';
import { Layout, Button, Menu, Dropdown, Avatar } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { ItemType } from 'antd/lib/menu/hooks/useItems';

import { UserRole } from 'enums';
import Accessor from 'components/Accessor';
import imgLogo from 'assets/img/logo-new.svg';
import homeLink from 'assets/img/homeLink.svg';
import homeLinkMobile from 'assets/img/homeLinkMobile.svg';

import {
  bullhornEnableSelector,
  isAdminSelector,
  userSelector,
  isMultiAccountSelector,
  isAdminAccessDeniedSelector,
  isTeammateAdminLevelSelector,
} from 'redux/selectors/authenticationSelectors';
import { getUserRoles, checkIsCompanyOwner } from 'utils/userRolesUtils';
import { TourStepEnum } from 'features/onboarding/enums';
import { ClientSections } from 'features/client/enums';
import { logout, switchActiveRole } from 'redux/reducers/authenticationSlice';
import { WorkersSectionsEnum } from 'features/worker/enums';
import { AssignmentSections } from 'features/assignment/enums';
import { CompanyProfileSection } from 'features/account/enums';
import { AdminPartnerSectionEnum } from 'features/admin/partner/enums';
import HeaderNotifications from 'features/notification/HeaderNotifications';
import { isTourStepActiveSelector } from 'redux/selectors/onboardingSelectors';
import { AdminRequestsSections } from 'features/admin/request/RequestsPage/enums';
import { AdminRecruiterSections } from 'features/admin/member/RecruitersPage/enums';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BullhornConnectModal from 'components/modals/BullhornConnectModal';
import { handleBullhornConnectModalOpen } from 'redux/reducers/bullhornSlice';
import { isBullhornConnectModalOpenSelector } from 'redux/selectors/bullhornSelector';

import styles from './AppHeader.module.less';

const { Header } = Layout;

export default function AppHeader() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const isAdminAccessDenied = useAppSelector(isAdminAccessDeniedSelector);
  const isMultiAccount = useAppSelector(isMultiAccountSelector);
  const bullhornEnable = useAppSelector(bullhornEnableSelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const isBullhornConnectModalOpen = useAppSelector(
    isBullhornConnectModalOpenSelector,
  );
  const isAccountSettingsStepActive = useAppSelector(
    isTourStepActiveSelector(TourStepEnum.AccountSettings),
  );
  const isHomeDashboardStepActive = useAppSelector(
    isTourStepActiveSelector(TourStepEnum.HomeDashboard),
  );
  const isTeammateAdminLevel = useAppSelector(isTeammateAdminLevelSelector);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleLogout = useCallback(() => {
    dispatch(logout());

    navigate('/login');
  }, [dispatch, navigate]);

  const handleSwitchRole = useCallback(() => {
    navigate('/');
    dispatch(switchActiveRole());
  }, [navigate, dispatch]);

  const handleCloseBullhornConnectModal = () => {
    dispatch(handleBullhornConnectModalOpen(false));
  };

  const openBullhornConnectModal = useCallback(() => {
    if (bullhornEnable) {
      return navigate('/placements', { replace: true });
    }
    dispatch(handleBullhornConnectModalOpen(true));
  }, [bullhornEnable, dispatch, navigate]);

  const adminMenu = useMemo(() => {
    const items: ItemType[] = [
      {
        key: 'accountSettings',
        label: <Link to="/account-settings">Account Settings</Link>,
      },
      {
        key: 'achPayments',
        label: <Link to="/ach-payments">ACH payments</Link>,
      },
      {
        key: 'adminList',
        label: <Link to="/admins">Admin list</Link>,
      },
    ];

    if (isMultiAccount) {
      items.push({
        key: 'switchRoleUser',
        label: 'Switch to User view',
        onClick: handleSwitchRole,
      });
    }

    items.push({
      key: 'logOut',
      label: (
        <>
          <LogoutOutlined />
          Log out
        </>
      ),
      onClick: handleLogout,
    });

    return items;
  }, [handleLogout, handleSwitchRole, isMultiAccount]);

  const userMenu = useMemo(() => {
    const items: ItemType[] = [
      {
        key: 'companyProfile',
        label: (
          <Link
            to={`/company-profile?section=${CompanyProfileSection.PrimaryInfo}`}
          >
            Company profile
          </Link>
        ),
      },
      {
        key: 'accountSettings',
        label: <Link to={`/account-settings`}>Account Settings</Link>,
      },
    ];

    if (checkIsCompanyOwner(user) || isTeammateAdminLevel) {
      items.push({
        key: 'teammates',
        label: <Link to={`/teammates`}>Teammates</Link>,
      });
    }

    items.push({
      key: 'bullhornConnect',
      label: (
        <>
          Bullhorn
          {bullhornEnable && (
            <span className={styles.bullhornConnect}>Connected</span>
          )}
        </>
      ),
      onClick: openBullhornConnectModal,
    });

    if (isMultiAccount) {
      items.push({
        key: 'switchRoleAdmin',
        label: 'Switch to Admin view',
        onClick: handleSwitchRole,
      });
    }

    items.push({
      key: 'logOut',
      label: (
        <>
          <LogoutOutlined />
          Log out
        </>
      ),
      onClick: handleLogout,
    });

    return items;
  }, [
    bullhornEnable,
    handleLogout,
    handleSwitchRole,
    isMultiAccount,
    isTeammateAdminLevel,
    openBullhornConnectModal,
    user,
  ]);

  const getMenuByRole = () => {
    // default is recruiter user
    const items = isAdmin ? adminMenu : userMenu;

    return <Menu className={styles.profileMenu} items={items} />;
  };

  const renderNavigation = () => {
    return (
      <>
        <li>
          <Link
            className={cx({
              'active-link': pathname === '/',
              'onboarding-spotlight': isHomeDashboardStepActive,
            })}
            to="/"
          >
            Home
          </Link>
        </li>

        <Accessor roles={getUserRoles(UserRole.RecruiterCompany)}>
          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/customers',
              })}
              to={`/customers?section=${ClientSections.Approved}`}
            >
              <i className="icon icon-client" />
              <span className={styles.text}>Customers</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/assignments',
              })}
              to={`/assignments?section=${AssignmentSections.AllAssignments}`}
            >
              <i className="icon icon-assignmant" />
              <span className={styles.text}>Assignments</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/workers',
              })}
              to={`/workers?section=${WorkersSectionsEnum.Workers}`}
            >
              <i className="icon icon-internal" />
              <span className={styles.text}>Workers</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/partners',
              })}
              to="/partners"
            >
              <i className="icon icon-codepen" />
              <span className={styles.text}>Partners</span>
            </Link>
          </li>

          {/* <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/team',
              })}
              to={`/team?section=${TeamSectionEnum.Recruiters}`}
            >
              <i className="icon icon-internal" />
              <span className={styles.text}>Team</span>
            </Link>
          </li> */}
        </Accessor>

        <Accessor roles={getUserRoles(UserRole.CorporateTA)}>
          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/assignments',
              })}
              to={`/assignments?section=${AssignmentSections.AllAssignments}`}
            >
              <i className="icon icon-assignmant" />
              <span className={styles.text}>Assignments</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/workers',
              })}
              to={`/workers?section=${WorkersSectionsEnum.Workers}`}
            >
              <i className="icon icon-internal" />
              <span className={styles.text}>Workers</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/partners',
              })}
              to="/partners"
            >
              <i className="icon icon-codepen" />
              <span className={styles.text}>Partners</span>
            </Link>
          </li>

          {/* <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/team',
              })}
              to={`/team?section=${TeamSectionEnum.Recruiters}`}
            >
              <i className="icon icon-internal" />
              <span className={styles.text}>Team</span>
            </Link>
          </li> */}
        </Accessor>

        <Accessor roles={getUserRoles(UserRole.Admin)}>
          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/todo',
              })}
              to={`/todo?section=${AdminRequestsSections.Members}`}
            >
              <i className="icon icon-work" />
              <span className={styles.text}>To Do</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/members',
              })}
              to={`/members?section=${AdminRecruiterSections.Recruiters}`}
            >
              <i className="icon icon-people" />
              <span className={styles.text}>Members</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/customers',
              })}
              to={`/customers?section=${ClientSections.Approved}`}
            >
              <i className="icon icon-client" />
              <span className={styles.text}>Customers</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/assignments',
              })}
              to={`/assignments?section=${AssignmentSections.AllAssignments}`}
            >
              <span className="icon icon-assignmant" />
              <span className={styles.text}>Assignments</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/workers',
              })}
              to={`/workers?section=${WorkersSectionsEnum.Approved}`}
            >
              <i className="icon icon-internal" />
              <span className={styles.text}>Workers</span>
            </Link>
          </li>

          <li className={styles.withIcon}>
            <Link
              className={cx({
                'active-link': pathname === '/partners',
              })}
              to={`/partners?section=${AdminPartnerSectionEnum.withMP}`}
            >
              <span className="icon icon-codepen" />
              <span className={styles.text}>Partners</span>
            </Link>
          </li>
        </Accessor>
      </>
    );
  };

  return (
    <>
      {isBullhornConnectModalOpen && (
        <BullhornConnectModal onClose={handleCloseBullhornConnectModal} />
      )}
      {(!user || isAdminAccessDenied) && (
        <Header className={styles.header}>
          <div className={styles.wrapper}>
            <ul>
              <li className="logo">
                <a
                  href={`${process.env.REACT_APP_LANDING_URL}/financial-eor-compliance`}
                >
                  <img className={styles.logo} src={imgLogo} alt="logo" />
                </a>
              </li>

              <li>
                <a
                  href={`${process.env.REACT_APP_LANDING_URL}/accounting-financial-payroll`}
                >
                  End-to-End Funding
                </a>
              </li>

              <li>
                <a
                  href={`${process.env.REACT_APP_LANDING_URL}/eor-employer-of-record-services`}
                >
                  Turnkey EOR
                </a>
              </li>

              <li>
                <a
                  href={`${process.env.REACT_APP_LANDING_URL}/business-compliance-services`}
                >
                  Dependable Compliance
                </a>
              </li>
            </ul>

            <ul>
              <li>
                <a href={process.env.REACT_APP_LANDING_URL}>
                  <img
                    src={homeLink}
                    className={cx(styles.homeLink, 'hidden-sm-down')}
                    alt="logo"
                  />
                  <img
                    src={homeLinkMobile}
                    className={cx(styles.homeLink, 'hidden-sm-up')}
                    alt="logo"
                  />
                </a>
              </li>

              <li>
                <Link to="/login">
                  <span className="icon icon-locked" />
                  <span>Member Login</span>
                </Link>
              </li>

              <li>
                <Link to="/registration">
                  <span className="icon icon-add-list" />
                  <span>Register</span>
                </Link>
              </li>

              <li className={styles.mobileMenu}>
                <span
                  className="icon icon-burger-menu"
                  onClick={() => setIsPanelOpen(!isPanelOpen)}
                />

                <div className={cx(styles.panel, { open: isPanelOpen })}>
                  <div className={styles.panelWrapper}>
                    <Link to="/">myProducts</Link>
                    <Link to="/">mySupport</Link>
                    <Link to="/">myBlog</Link>
                    <Link to="/">About Us</Link>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </Header>
      )}

      {!isAdminAccessDenied && user && (
        <Header className={styles.root}>
          <div className={styles.logoBox}>
            <Link to="/">
              <img src={imgLogo} alt="logo" />
            </Link>
          </div>

          <ul className={styles.navList}>{renderNavigation()}</ul>

          <div
            className={cx(styles.rightPart, {
              'onboarding-spotlight': isAccountSettingsStepActive,
            })}
          >
            <ul className={styles.iconList}>
              <li>
                <HeaderNotifications />
              </li>
            </ul>

            <div className={styles.profileDropdownBox}>
              <Dropdown overlay={getMenuByRole()} trigger={['click']}>
                <Button
                  className={cx(styles.dropdownBtn, 'borderless-btn icon-btn')}
                >
                  <Avatar
                    size={24}
                    icon={<span className="icon icon-user" />}
                  />
                </Button>
              </Dropdown>
            </div>
          </div>

          <div className={styles.searchBox}>
            <span className={styles.sidebarToggler}>
              <i className="icon icon-burger-menu" />
            </span>
          </div>
        </Header>
      )}
    </>
  );
}
